/**
 * Profile module API — Supabase queries for profiles, follows, achievements, heatmap.
 * All functions use .throwOnError() per project rules.
 * All external data validated with Zod — no bare `as` casts.
 */

import { z } from 'zod'
import { supabase } from '@turnifly/shared/api'

// ── PROFILE ─────────────────────────────────────────────────

export async function fetchProfile(userId: string): Promise<unknown> {
  const { data } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .is('deleted_at', null)
    .single()
    .throwOnError()
  return data
}

/** Fields that clients are allowed to update */
const ALLOWED_UPDATE_FIELDS = new Set([
  'full_name', 'bio', 'link_title', 'link_url', 'profession',
  'interests', 'is_private', 'show_region', 'show_gender', 'show_age',
  'language', 'notifications_enabled', 'dark_mode', 'timezone',
  'birth_date', 'life_expectancy', 'garden_type', 'banner_template',
  'banner_url', 'banner_position_y', 'habit_view_mode',
  'onboarding_completed', 'interests_asked', 'interests_ask_date',
  'avatar_url',
])

export async function updateProfile(
  userId: string,
  updates: Record<string, unknown>,
): Promise<unknown> {
  const safeUpdates: Record<string, unknown> = {}
  for (const key of Object.keys(updates)) {
    if (ALLOWED_UPDATE_FIELDS.has(key)) {
      safeUpdates[key] = updates[key]
    }
  }

  const { data } = await supabase
    .from('profiles')
    .update(safeUpdates)
    .eq('id', userId)
    .select()
    .single()
    .throwOnError()
  return data
}

export async function softDeleteProfile(userId: string): Promise<void> {
  await supabase
    .from('profiles')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', userId)
    .throwOnError()
}

// ── FOLLOW SYSTEM ───────────────────────────────────────────

export interface FollowStatusResult {
  isFollowing: boolean
  isFollowedBy: boolean
  followStatus: 'accepted' | 'pending' | 'none'
  isBlockedByTarget: boolean
}

export async function fetchFollowStatus(
  myId: string,
  targetId: string,
): Promise<FollowStatusResult> {
  const [outgoing, incoming, blocked] = await Promise.all([
    supabase
      .from('follows')
      .select('status')
      .eq('follower_id', myId)
      .eq('following_id', targetId)
      .maybeSingle()
      .throwOnError(),
    supabase
      .from('follows')
      .select('status')
      .eq('follower_id', targetId)
      .eq('following_id', myId)
      .maybeSingle()
      .throwOnError(),
    supabase
      .rpc('is_blocked_by', { p_target_id: targetId })
      .throwOnError(),
  ])

  const outStatus = outgoing.data?.status as string | undefined

  return {
    isFollowing: outStatus === 'accepted',
    isFollowedBy: incoming.data?.status === 'accepted',
    followStatus: outStatus === 'accepted' ? 'accepted'
      : outStatus === 'pending' ? 'pending'
      : 'none',
    isBlockedByTarget: blocked.data === true,
  }
}

/**
 * Follow a user via RPC (bypasses RLS upsert issue).
 * Counter sync is handled by DB trigger (trg_follow_counts).
 */
export async function followUser(
  _followerId: string,
  followingId: string,
  isTargetPrivate: boolean,
): Promise<void> {
  await supabase
    .rpc('follow_user', {
      p_following_id: followingId,
      p_is_private: isTargetPrivate,
    })
    .throwOnError()
}

/**
 * Unfollow a user via RPC (bypasses RLS delete issue).
 * Counter sync is handled by DB trigger (trg_follow_counts).
 */
export async function unfollowUser(
  _followerId: string,
  followingId: string,
): Promise<void> {
  await supabase
    .rpc('unfollow_user', { p_following_id: followingId })
    .throwOnError()
}

// ── FOLLOWERS / FOLLOWING LISTS ─────────────────────────────

interface PaginatedUsersParams {
  userId: string
  limit?: number
  offset?: number
}

const FollowListItemSchema = z.object({
  id: z.string().uuid(),
  full_name: z.string(),
  avatar_url: z.string().nullable(),
  badge_type: z.string().nullable(),
  username: z.string().nullable(),
})

export type FollowListItem = z.infer<typeof FollowListItemSchema>

export async function fetchFollowers({
  userId, limit = 20, offset = 0,
}: PaginatedUsersParams): Promise<FollowListItem[]> {
  const { data } = await supabase
    .from('followers_view')
    .select('profile_id, full_name, avatar_url, badge_type, username')
    .eq('following_id', userId)
    .eq('status', 'accepted')
    .range(offset, offset + limit - 1)
    .throwOnError()

  return (data ?? []).map((row) => {
    const r = row as Record<string, unknown>
    return FollowListItemSchema.parse({ id: r.profile_id, ...r })
  })
}

export async function fetchFollowing({
  userId, limit = 20, offset = 0,
}: PaginatedUsersParams): Promise<FollowListItem[]> {
  const { data } = await supabase
    .from('following_view')
    .select('profile_id, full_name, avatar_url, badge_type, username')
    .eq('follower_id', userId)
    .eq('status', 'accepted')
    .range(offset, offset + limit - 1)
    .throwOnError()

  return (data ?? []).map((row) => {
    const r = row as Record<string, unknown>
    return FollowListItemSchema.parse({ id: r.profile_id, ...r })
  })
}

// ── ACTIVITY HEATMAP ────────────────────────────────────────

const HeatmapEntrySchema = z.object({
  activity_date: z.string(),
  activity_count: z.number(),
})

export type HeatmapEntry = z.infer<typeof HeatmapEntrySchema>

/**
 * Activity heatmap — server-side privacy via get_user_heatmap_safe RPC.
 * Returns empty array when viewer cannot see target's content.
 */
export async function fetchActivityHeatmap(
  userId: string,
  year: number,
): Promise<HeatmapEntry[]> {
  const { data } = await supabase
    .rpc('get_user_heatmap_safe', { p_target_user_id: userId, p_year: year })
    .throwOnError()

  return (data ?? []).map((entry: unknown) => HeatmapEntrySchema.parse(entry))
}

// ── ACHIEVEMENTS ────────────────────────────────────────────

const UserAchievementWithDetailsSchema = z.object({
  achievement_id: z.string().uuid(),
  year: z.number(),
  progress: z.number(),
  status: z.string(),
  achieved_at: z.string().nullable(),
  achievement: z.object({
    key: z.string(),
    name_uz: z.string(),
    name_en: z.string(),
    icon: z.string(),
    category: z.string(),
    target_value: z.number(),
  }),
})

export type UserAchievementWithDetails = z.infer<typeof UserAchievementWithDetailsSchema>

/**
 * Achievements — server-side privacy via get_user_achievements_safe RPC.
 * Returns empty array when viewer cannot see target's content.
 */
export async function fetchUserAchievements(
  userId: string,
  year?: number,
): Promise<UserAchievementWithDetails[]> {
  const { data } = await supabase
    .rpc('get_user_achievements_safe', {
      p_target_user_id: userId,
      p_year: year ?? null,
    })
    .throwOnError()

  return (data ?? []).map((row: unknown) => UserAchievementWithDetailsSchema.parse(row))
}

// ── USER STATS (aggregated) ─────────────────────────────────

const UserStatsResultSchema = z.object({
  totalScore: z.number(),
  currentStreak: z.number(),
  activeDays: z.number(),
  visible: z.boolean().catch(true),
})

export type UserStatsResult = z.infer<typeof UserStatsResultSchema>

/**
 * Aggregated stats — server-side privacy via get_user_stats_safe RPC.
 * Returns zeros + visible:false when viewer cannot see target's content.
 */
export async function fetchUserStats(userId: string): Promise<UserStatsResult> {
  const { data } = await supabase
    .rpc('get_user_stats_safe', { p_target_user_id: userId })
    .throwOnError()
  return UserStatsResultSchema.parse(data)
}

// ── USER VISIBILITY STATE (for profile gating) ─────────────

const UserVisibilityStateSchema = z.object({
  exists: z.boolean(),
  is_self: z.boolean().catch(false),
  is_private: z.boolean().catch(false),
  follow_status: z.enum(['accepted', 'pending', 'none']).catch('none'),
  is_followed_by: z.boolean().catch(false),
  is_blocked_by_target: z.boolean().catch(false),
  has_blocked_target: z.boolean().catch(false),
  can_see_content: z.boolean().catch(false),
})

export type UserVisibilityState = z.infer<typeof UserVisibilityStateSchema>

/**
 * Authoritative answer for "can I see this user's content?".
 * Used by the profile lock screen to choose between full view, lock,
 * blocked, or not-found UI states.
 */
export async function fetchUserVisibilityState(
  targetUserId: string,
): Promise<UserVisibilityState> {
  const { data } = await supabase
    .rpc('get_user_visibility_state', { p_target_id: targetUserId })
    .throwOnError()
  return UserVisibilityStateSchema.parse(data)
}

// ── BLOCK / UNBLOCK ────────────────────────────────────────

export async function blockUser(blockerId: string, blockedId: string): Promise<void> {
  // Remove any follow relationship first
  await supabase
    .from('follows')
    .delete()
    .or(`and(follower_id.eq.${blockerId},following_id.eq.${blockedId}),and(follower_id.eq.${blockedId},following_id.eq.${blockerId})`)
    .throwOnError()

  await supabase
    .from('user_blocks')
    .insert({ blocker_id: blockerId, blocked_id: blockedId })
    .throwOnError()
}

export async function unblockUser(blockerId: string, blockedId: string): Promise<void> {
  await supabase
    .from('user_blocks')
    .delete()
    .eq('blocker_id', blockerId)
    .eq('blocked_id', blockedId)
    .throwOnError()
}

export async function fetchBlockedUsers(userId: string): Promise<FollowListItem[]> {
  const { data } = await supabase
    .from('user_blocks')
    .select('blocked_id')
    .eq('blocker_id', userId)
    .throwOnError()

  if (!data?.length) return []

  const blockedIds = (data as Array<{ blocked_id: string }>).map((b) => b.blocked_id)
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, full_name, avatar_url, badge_type, username')
    .in('id', blockedIds)
    .throwOnError()

  return (profiles ?? []).map((p) => FollowListItemSchema.parse(p))
}

// ── FOLLOW REQUEST MANAGEMENT (private accounts) ──────────

export interface PendingFollowRequest {
  follower_id: string
  full_name: string
  avatar_url: string | null
  badge_type: string | null
  username: string | null
  created_at: string
}

export async function fetchPendingFollowRequests(userId: string): Promise<PendingFollowRequest[]> {
  const { data } = await supabase
    .rpc('get_pending_follow_requests', { p_user_id: userId })
    .throwOnError()
  return (data ?? []) as PendingFollowRequest[]
}

export async function acceptFollowRequest(followerId: string, _followingId: string): Promise<void> {
  await supabase
    .rpc('accept_follow_request', { p_follower_id: followerId })
    .throwOnError()
}

export async function rejectFollowRequest(followerId: string, _followingId: string): Promise<void> {
  await supabase
    .rpc('reject_follow_request', { p_follower_id: followerId })
    .throwOnError()
}
